/* ============================================================
   HERCULES — lp-simple-bundles.js

   Shared adapter for Simple Bundles & Kits component selection.

   WHY THIS EXISTS
   Simple Bundles does not expose an API. It injects <select> elements
   into the theme's product form, and a bundle only reaches the cart
   correctly if three things are maintained together:

     1. the native <select> value
     2. a hidden input  properties[Raw Label]  per component
     3. a single hidden properties[_bundle_selection], a " <> "
        joined string in the SAME ORDER as the selects in the form

   Getting any of those wrong produces a bundle that adds to cart and
   looks fine but ships the wrong components. That is why this is one
   module rather than a second implementation.

   PROVENANCE
   The label derivation, option filtering, hidden-input resolution and
   the " <> " join are lifted verbatim from assets/lp-bundle-buybox.js
   (getRawLabelForSelect, cssEscape, cacheDom, updateBundleSelectionInput).
   That file still has its own copy and is untouched - it works and
   takes live orders. If it is ever refactored, it should call this
   module and the copies collapse to one. Until then: FIX BOTH.

   USAGE
     const sb = new SimpleBundlesAdapter(form);
     sb.ready().then(components => { ... render your own UI ... });
     sb.select(rawLabel, value);      // writes all three places
     sb.formData(variantId, qty, planId);  // payload for /cart/add.js

   Submission uses FormData, not JSON: component labels contain
   characters that do not survive a JSON round-trip cleanly, and
   Shopify's /cart/add.js accepts FormData directly.
   ============================================================ */

(function () {
  'use strict';

  // Simple Bundles renders into one of these two containers depending on
  // app version. Both are checked; neither is guaranteed to exist at
  // DOMContentLoaded because injection is asynchronous.
  const SELECT_QUERY =
    '[data-simple-bundles-options] select, #simple-bundles-io-options select';

  const JOIN = ' <> ';

  function cssEscape(str) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
      return CSS.escape(str);
    }
    return String(str).replace(/[!"#$%&'()*+,./:;<=>?@[\\\]^`{|}~]/g, '\\$&');
  }

  class SimpleBundlesAdapter {
    constructor(form) {
      this.form = form;
      this.components = [];
      this.selections = {};
      this.bundleSelectionInput = null;
      this.propertyInputs = {};
    }

    /* Simple Bundles injects asynchronously, so poll rather than assume.
       Resolves with [] when the product simply is not a bundle - callers
       must treat an empty array as "not a bundle", not as an error. */
    ready(timeoutMs = 4000, intervalMs = 100) {
      return new Promise((resolve) => {
        if (!this.form) return resolve([]);

        const started = Date.now();
        const poll = () => {
          const selects = this.form.querySelectorAll(SELECT_QUERY);
          if (selects.length > 0) {
            this.cache(Array.from(selects));
            return resolve(this.components);
          }
          if (Date.now() - started >= timeoutMs) return resolve([]);
          setTimeout(poll, intervalMs);
        };
        poll();
      });
    }

    cache(selects) {
      this.components = selects.map((select) => {
        const rawLabel = this.rawLabelFor(select);

        // Skip the disabled "Please select" placeholder, and treat an
        // explicit disabled attribute OR inventory <= 0 as unavailable -
        // Simple Bundles sets disabled itself for out-of-stock variants.
        const options = Array.from(select.options)
          .filter((opt) => opt.value !== '' && !opt.hidden)
          .map((opt) => {
            const inv = parseInt(opt.dataset.inventory, 10);
            return {
              value: opt.value,
              label: opt.textContent.trim(),
              unavailable: opt.disabled || (Number.isFinite(inv) && inv <= 0)
            };
          });

        // Seed from whatever Simple Bundles already selected; otherwise take
        // the first in-stock option so the bundle is never in a state with
        // no component chosen.
        const selected = Array.from(select.options).find(
          (o) => o.selected && o.value !== ''
        );
        if (selected) {
          this.selections[rawLabel] = selected.value;
        } else {
          const firstAvailable = options.find((o) => !o.unavailable);
          if (firstAvailable) {
            this.selections[rawLabel] = firstAvailable.value;
            select.value = firstAvailable.value;
          }
        }

        return { select, rawLabel, options };
      });

      this.bundleSelectionInput = this.form.querySelector(
        'input[name="properties[_bundle_selection]"]'
      );

      /* The <select> and the hidden <input> share a name. When both are in
         the form the LATER one wins on submit, so the hidden inputs are the
         real payload - take the last of each. */
      this.components.forEach(({ rawLabel }) => {
        const inputs = this.form.querySelectorAll(
          'input[type="hidden"][name="properties[' + cssEscape(rawLabel) + ']"]'
        );
        if (inputs.length) this.propertyInputs[rawLabel] = inputs[inputs.length - 1];
      });

      this.sync();
    }

    rawLabelFor(select) {
      if (select.id) {
        const labelEl = this.form.querySelector(
          'label[for="' + cssEscape(select.id) + '"]'
        );
        if (labelEl && labelEl.textContent.trim()) return labelEl.textContent.trim();
      }
      const match = (select.name || '').match(/^properties\[(.+)\]$/);
      return match ? match[1] : select.name || 'Component';
    }

    /* Write a choice to all three places. Order of the join must match the
       order of the selects in the form - Simple Bundles fixed that at render
       time, so components[] order is authoritative and must not be sorted. */
    select(rawLabel, value) {
      this.selections[rawLabel] = value;

      const entry = this.components.find((c) => c.rawLabel === rawLabel);
      if (entry) {
        entry.select.value = value;
        // Let the app react if it is listening; harmless if it is not.
        entry.select.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.sync();
    }

    sync() {
      this.components.forEach(({ rawLabel }) => {
        const input = this.propertyInputs[rawLabel];
        if (input) input.value = this.selections[rawLabel] || '';
      });
      if (this.bundleSelectionInput) {
        this.bundleSelectionInput.value = this.components
          .map(({ rawLabel }) => this.selections[rawLabel] || '')
          .join(JOIN);
      }
    }

    /* Build the add-to-cart payload from the live form, so every hidden input
       Simple Bundles maintains is carried, then override the bits the buy box
       owns. FormData rather than JSON - component labels contain characters
       that do not round-trip cleanly. */
    formData(variantId, quantity, sellingPlanId) {
      const fd = new FormData(this.form);
      if (variantId) fd.set('id', String(variantId));
      if (quantity) fd.set('quantity', String(quantity));
      if (sellingPlanId) {
        fd.set('selling_plan', String(sellingPlanId));
      } else {
        fd.delete('selling_plan');
      }
      return fd;
    }
  }

  window.SimpleBundlesAdapter = SimpleBundlesAdapter;
})();
